// 051 — Historia Clínica Materna Perinatal: Tanda 2, página 2 (Fase 4, ver
// PLAN.md "FASE 4 — spec completa"). Cierra 051: variables para
// identificar casos de near miss, recién nacido, egreso RN y egreso
// materno. El partograma en sí (dilatación×horas + curva de alerta) vive
// aparte en `partograma/` (componente visual bespoke + `diagramaPartograma`
// para el PDF) — este archivo solo cubre lo que sigue siendo `Par[]` de
// respaldo, mismo criterio que `bloques051PartoDatos.ts` (Tanda 1b): sin
// recurso FHIR nuevo salvo que ya exista un consumidor clínico real (ver
// `partograma/fhir.ts` para la única excepción de esta tanda).
import type { Par } from './index';
import { conCodigoCie, leerSiNo, textoSiNo, type SiNoValor } from './camposSimplesDatos';
import type { DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import type { Traducir } from './tecnicaAnestesicaDatos';

function paresDeChecklist(t: Traducir, prefijoI18n: string, opciones: readonly string[], valores: Readonly<Record<string, boolean>>, prefijoOpciones: string = prefijoI18n): Par {
  return { pregunta: t(`${prefijoI18n}.titulo`), respuesta: opciones.filter((c) => valores[c]).map((c) => t(`${prefijoOpciones}.opciones.${c}`)).join(', ') };
}

function si(t: Traducir, v: boolean): string {
  return v ? t('formulario.si') : '';
}

function etiquetaSiNo(t: Traducir, prefijoI18nOpciones: string, v: string): string {
  return v ? t(`${prefijoI18nOpciones}.${v}`) : '';
}

/** Casilla SI/NO del papel guardada como `SiNoValor`: el "No" contestado llega al `Par[]` firmado. */
function siNo(t: Traducir, v: SiNoValor): string {
  return textoSiNo(v, { si: t('formulario.si'), no: t('formulario.no') });
}

function esObjeto(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function textoLibre(v: unknown): string {
  return typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
}

/** Mapa clave → SiNoValor desde el modelo nuevo (`'si'|'no'`) o el viejo (`true`: el viejo no distinguía el No, queda sin responder). */
function leerMapaSiNo(v: unknown): Record<string, SiNoValor> {
  if (!esObjeto(v)) return {};
  return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, leerSiNo(x)] as const).filter(([, x]) => x !== ''));
}

function leerMapaBool(v: unknown): Record<string, boolean> {
  if (!esObjeto(v)) return {};
  return Object.fromEntries(Object.entries(v).filter(([, x]) => x === true).map(([k]) => [k, true] as const));
}

// ---------- Variables para identificar casos de near miss ----------

export const NEAR_MISS_SI_NO_OPCIONES = ['si', 'no'] as const;
export const NEAR_MISS_LABORATORIO_OPCIONES = ['si', 'no', 'sd'] as const;

export const NEAR_MISS_CRITERIOS_CLINICOS_OPCIONES = [
  'shock',
  'paroCardiaco',
  'ictericiaPreeclampsia',
  'cianosisAguda',
  'respiracionJadeante',
  'taquipneaSevera',
  'bradipneaSevera',
  'oliguriaResistente',
  'alteracionesCoagulacion',
  'coma',
  'inconscienciaProlongada',
  'accidenteCerebroVascular',
  'convulsionesIncontrolables',
  'paralisisGeneralizada',
] as const;

export const NEAR_MISS_LABORATORIO_ITEMS_OPCIONES = ['plaquetasBajas', 'creatininaAlta', 'bilirrubinaAlta', 'phBajo', 'po2Bajo', 'paO2FiO2Bajo', 'lactatoAlto'] as const;

export const NEAR_MISS_INTERVENCIONES_SIMPLES_OPCIONES = ['hemoderivados3Vol', 'ingresoUci7Dias', 'histerectomia', 'dialisisIra', 'reanimacionCardiopulmonar'] as const;

export interface EstadoNearMiss051 {
  readonly presente: string;
  readonly criteriosClinicos: Readonly<Record<string, string>>;
  readonly laboratorio: Readonly<Record<string, string>>;
  readonly agentesVasoactivos: string;
  readonly agentesVasoactivosCual: string;
  readonly intubacionVentilacion: string;
  readonly intubacionVentilacionDias: string;
  readonly intervencionesSimples: Readonly<Record<string, string>>;
  /** Papel: la casilla "días" del bloque de intervenciones cuelga de "Ingreso de UCI > 7 días". */
  readonly ingresoUciDias: string;
}

export function estadoInicialNearMiss051(): EstadoNearMiss051 {
  return {
    presente: '',
    criteriosClinicos: {},
    laboratorio: {},
    agentesVasoactivos: '',
    agentesVasoactivosCual: '',
    intubacionVentilacion: '',
    intubacionVentilacionDias: '',
    intervencionesSimples: {},
    ingresoUciDias: '',
  };
}

/**
 * "Variables identificar casos de near miss: no / si" es el padre de todo lo demás: en "No" no se imprime ningún criterio (evita
 * firmar un criterio "Sí" huérfano). Agentes cuál/es, días de intubación y días de UCI solo salen si su padre está en "Sí".
 */
function paresDeNearMiss051(t: Traducir, e: EstadoNearMiss051): Par[] {
  const n = 'formularios.f051.nearMiss';
  const pares: Par[] = [{ pregunta: t(`${n}.presente`), respuesta: etiquetaSiNo(t, `${n}.opcionesSiNo`, e.presente) }];
  if (e.presente === 'no') return pares;
  for (const c of NEAR_MISS_CRITERIOS_CLINICOS_OPCIONES) {
    pares.push({ pregunta: t(`${n}.criteriosClinicos.${c}`), respuesta: etiquetaSiNo(t, `${n}.opcionesSiNo`, e.criteriosClinicos[c] ?? '') });
  }
  for (const l of NEAR_MISS_LABORATORIO_ITEMS_OPCIONES) {
    pares.push({ pregunta: t(`${n}.laboratorio.${l}`), respuesta: etiquetaSiNo(t, `${n}.opcionesLaboratorio`, e.laboratorio[l] ?? '') });
  }
  pares.push({ pregunta: t(`${n}.agentesVasoactivos`), respuesta: etiquetaSiNo(t, `${n}.opcionesSiNo`, e.agentesVasoactivos) });
  if (e.agentesVasoactivos === 'si') pares.push({ pregunta: t(`${n}.agentesVasoactivosCual`), respuesta: e.agentesVasoactivosCual.trim() });
  pares.push({ pregunta: t(`${n}.intubacionVentilacion`), respuesta: etiquetaSiNo(t, `${n}.opcionesSiNo`, e.intubacionVentilacion) });
  if (e.intubacionVentilacion === 'si') pares.push({ pregunta: t(`${n}.intubacionVentilacionDias`), respuesta: e.intubacionVentilacionDias.trim() });
  for (const iv of NEAR_MISS_INTERVENCIONES_SIMPLES_OPCIONES) {
    const v = e.intervencionesSimples[iv] ?? '';
    pares.push({ pregunta: t(`${n}.intervencionesSimples.${iv}`), respuesta: etiquetaSiNo(t, `${n}.opcionesSiNo`, v) });
    if (iv === 'ingresoUci7Dias' && v === 'si') pares.push({ pregunta: t(`${n}.ingresoUciDias`), respuesta: e.ingresoUciDias.trim() });
  }
  return pares;
}

// ---------- Recién nacido ----------

export const SEXO_RN_OPCIONES = ['h', 'm', 'noDefinido'] as const;
export type SexoRn = (typeof SEXO_RN_OPCIONES)[number];

export const EG_FUENTE_RN_OPCIONES = ['fum', 'eco', 'estimada'] as const;
export type EgFuenteRn = (typeof EG_FUENTE_RN_OPCIONES)[number];

export const PESO_EG_RN_OPCIONES = ['adecuado', 'pequeno', 'grande'] as const;
export type PesoEgRn = (typeof PESO_EG_RN_OPCIONES)[number];

/** Reanimación: cada ítem es no / si en el papel (máscara y bolsa son dos ítems distintos). */
export const REANIMACION_RN_OPCIONES = ['estimulacion', 'aspiracion', 'mascara', 'bolsa', 'tuboEndotraqueal', 'masajeCardiaco', 'medicacion'] as const;

/** Peso al nacer: los dos círculos de alerta impresos junto al recuadro (< 2500 g / > 4000 g). */
export const PESO_NACER_ALERTA_OPCIONES = ['menor2500', 'mayor4000'] as const;
export type PesoNacerAlerta = (typeof PESO_NACER_ALERTA_OPCIONES)[number];

export const ATENDIO_RN_OPCIONES = ['medico', 'obstetriz', 'enfermera', 'auxiliar', 'estudiante', 'empirica', 'otro'] as const;

/** Papel: "aloj. conj." y "neonatolog." (no hay tocología). */
export const TRASLADO_RN_OPCIONES = ['alojamientoConjunto', 'neonatologia'] as const;
export type TrasladoRn = (typeof TRASLADO_RN_OPCIONES)[number];

/** Papel: tres círculos "no / menor / mayor" sobre un solo recuadro CIE 10. */
export const DEFECTOS_CONGENITOS_OPCIONES = ['no', 'menor', 'mayor'] as const;
export type DefectosCongenitos = (typeof DEFECTOS_CONGENITOS_OPCIONES)[number];

export const ENFERMEDADES_RN_OPCIONES = ['ninguna', 'unoOMas'] as const;
export type EnfermedadesRn = (typeof ENFERMEDADES_RN_OPCIONES)[number];

export const TAMIZAJE_METABOLICO_OPCIONES = ['seRealiza', 'noSeRealiza', 'noAplica'] as const;
export type TamizajeMetabolico = (typeof TAMIZAJE_METABOLICO_OPCIONES)[number];

export const TAMIZAJE_AUDITIVO_OPCIONES = ['pasaOi', 'pasaOd', 'noPasaOi', 'noPasaOd', 'noSeHace'] as const;

export interface EstadoRecienNacido051 {
  readonly sexo: SexoRn | '';
  readonly pesoAlNacer: string;
  readonly pesoAlNacerAlerta: PesoNacerAlerta | '';
  readonly longitud: string;
  readonly perimetroCefalico: string;
  readonly edadGestacionalSemanas: string;
  readonly edadGestacionalDias: string;
  readonly egFuente: EgFuenteRn | '';
  readonly pesoEg: PesoEgRn | '';
  readonly apgar1: string;
  readonly apgar5: string;
  readonly apgar10: string;
  readonly apgarNoAplica: boolean;
  readonly reanimacion: Readonly<Record<string, SiNoValor>>;
  readonly falleceEnLugarParto: string;
  readonly atendioParto: Readonly<Record<string, boolean>>;
  readonly atendioNeonato: Readonly<Record<string, boolean>>;
  readonly atendioNombre: string;
  readonly traslado: TrasladoRn | '';
  readonly tipificacionRn: string;
  readonly vitaminaK: string;
  readonly profilaxisOcular: string;
  readonly metodoCanguroAmerita: string;
  readonly metodoCanguroSeAplica: string;
  readonly examenFisicoNormal: string;
  readonly defectosCongenitos: DefectosCongenitos | '';
  readonly defectosCongenitosCie10: string;
  readonly enfermedades: EnfermedadesRn | '';
  /** Papel: dos filas de CIE 10 (código + descripción) bajo "Enfermedades". */
  readonly enfermedadesDx: readonly [DiagnosticoCie10Valor, DiagnosticoCie10Valor];
  readonly tamizajeMetabolico: TamizajeMetabolico | '';
  readonly tamizajeAuditivo: Readonly<Record<string, boolean>>;
  readonly vacunaHepatitisBDosis0: string;
  readonly apegoInmediato: string;
  readonly meconio1erDia: string;
  readonly lactanciaInmediata: string;
}

const dxVacioRn = (): DiagnosticoCie10Valor => ({ texto: '', cie10: null });

export function estadoInicialRecienNacido051(): EstadoRecienNacido051 {
  return {
    sexo: '',
    pesoAlNacer: '',
    pesoAlNacerAlerta: '',
    longitud: '',
    perimetroCefalico: '',
    edadGestacionalSemanas: '',
    edadGestacionalDias: '',
    egFuente: '',
    pesoEg: '',
    apgar1: '',
    apgar5: '',
    apgar10: '',
    apgarNoAplica: false,
    reanimacion: {},
    falleceEnLugarParto: '',
    atendioParto: {},
    atendioNeonato: {},
    atendioNombre: '',
    traslado: '',
    tipificacionRn: '',
    vitaminaK: '',
    profilaxisOcular: '',
    metodoCanguroAmerita: '',
    metodoCanguroSeAplica: '',
    examenFisicoNormal: '',
    defectosCongenitos: '',
    defectosCongenitosCie10: '',
    enfermedades: '',
    enfermedadesDx: [dxVacioRn(), dxVacioRn()],
    tamizajeMetabolico: '',
    tamizajeAuditivo: {},
    vacunaHepatitisBDosis0: '',
    apegoInmediato: '',
    meconio1erDia: '',
    lactanciaInmediata: '',
  };
}

/** Lee lo guardado del recién nacido: la reanimación vieja era una checklist booleana con "máscara/bolsa" junta. */
export function leerRecienNacido051(valor: unknown): EstadoRecienNacido051 {
  const base = estadoInicialRecienNacido051();
  if (!esObjeto(valor)) return base;
  const reanimacion = leerMapaSiNo(valor.reanimacion);
  if (reanimacion.mascaraBolsa) {
    // Modelo viejo: una sola casilla "Máscara/bolsa": se conserva marcada en las dos del papel.
    reanimacion.mascara = reanimacion.mascara ?? reanimacion.mascaraBolsa;
    reanimacion.bolsa = reanimacion.bolsa ?? reanimacion.mascaraBolsa;
    delete reanimacion.mascaraBolsa;
  }
  // Defectos congénitos: el modelo viejo era ninguno / "1 o más" + gravedad aparte; el papel trae no / menor / mayor.
  const { defectosCongenitosGravedad, enfermedadesCie10, ...resto } = valor;
  const defectosViejo = resto.defectosCongenitos;
  const defectosCongenitos: DefectosCongenitos | '' =
    defectosViejo === 'ninguno' || defectosViejo === 'no' ? 'no'
    : defectosViejo === 'menor' || defectosViejo === 'mayor' ? defectosViejo
    : defectosViejo === 'unoOMas' && (defectosCongenitosGravedad === 'menor' || defectosCongenitosGravedad === 'mayor') ? defectosCongenitosGravedad
    : '';
  // Enfermedades: el modelo viejo guardaba un solo texto; pasa a la primera fila y la segunda queda vacía.
  const dxNuevo = Array.isArray(resto.enfermedadesDx) ? resto.enfermedadesDx : [];
  const leerDx = (d: unknown, viejo: string): DiagnosticoCie10Valor => (esObjeto(d) && typeof d.texto === 'string' ? { texto: d.texto, cie10: esObjeto(d.cie10) && typeof d.cie10.code === 'string' ? (d.cie10 as unknown as DiagnosticoCie10Valor['cie10']) : null } : { texto: viejo, cie10: null });
  const enfermedadesDx: readonly [DiagnosticoCie10Valor, DiagnosticoCie10Valor] = [leerDx(dxNuevo[0], textoLibre(enfermedadesCie10)), leerDx(dxNuevo[1], '')];
  return { ...base, ...(resto as Partial<EstadoRecienNacido051>), defectosCongenitos, enfermedadesDx, defectosCongenitosCie10: textoLibre(resto.defectosCongenitosCie10), reanimacion, atendioParto: leerMapaBool(valor.atendioParto), atendioNeonato: leerMapaBool(valor.atendioNeonato), tamizajeAuditivo: leerMapaBool(valor.tamizajeAuditivo) };
}

function paresDeRecienNacido051(t: Traducir, e: EstadoRecienNacido051): Par[] {
  const r = 'formularios.f051.recienNacido';
  const opcionesSiNo = 'formularios.f051.nearMiss.opcionesSiNo';
  const pares: Par[] = [
    { pregunta: t(`${r}.sexo`), respuesta: e.sexo ? t(`${r}.sexoOpciones.${e.sexo}`) : '' },
    { pregunta: t(`${r}.pesoAlNacer`), respuesta: e.pesoAlNacer.trim() },
    { pregunta: t(`${r}.pesoAlNacerAlerta`), respuesta: e.pesoAlNacerAlerta ? t(`${r}.pesoAlNacerAlertaOpciones.${e.pesoAlNacerAlerta}`) : '' },
    { pregunta: t(`${r}.longitud`), respuesta: e.longitud.trim() },
    { pregunta: t(`${r}.perimetroCefalico`), respuesta: e.perimetroCefalico.trim() },
    { pregunta: t(`${r}.edadGestacionalSemanas`), respuesta: e.edadGestacionalSemanas.trim() },
    { pregunta: t(`${r}.edadGestacionalDias`), respuesta: e.edadGestacionalDias.trim() },
    { pregunta: t(`${r}.egFuente`), respuesta: e.egFuente ? t(`${r}.egFuenteOpciones.${e.egFuente}`) : '' },
    { pregunta: t(`${r}.pesoEg`), respuesta: e.pesoEg ? t(`${r}.pesoEgOpciones.${e.pesoEg}`) : '' },
    { pregunta: t(`${r}.apgar1`), respuesta: e.apgar1.trim() },
    { pregunta: t(`${r}.apgar5`), respuesta: e.apgar5.trim() },
    // Papel: el "n/a" va unido con una línea al recuadro de 10': si no aplica, ese valor no se imprime.
    { pregunta: t(`${r}.apgar10`), respuesta: e.apgarNoAplica ? '' : e.apgar10.trim() },
    { pregunta: t(`${r}.apgarNoAplica`), respuesta: si(t, e.apgarNoAplica) },
  ];
  for (const c of REANIMACION_RN_OPCIONES) pares.push({ pregunta: `${t(`${r}.reanimacion.titulo`)} — ${t(`${r}.reanimacion.opciones.${c}`)}`, respuesta: siNo(t, e.reanimacion[c] ?? '') });
  pares.push(
    { pregunta: t(`${r}.falleceEnLugarParto`), respuesta: etiquetaSiNo(t, opcionesSiNo, e.falleceEnLugarParto) },
    paresDeChecklist(t, `${r}.atendioParto`, ATENDIO_RN_OPCIONES, e.atendioParto, `${r}.atendio`),
    paresDeChecklist(t, `${r}.atendioNeonato`, ATENDIO_RN_OPCIONES, e.atendioNeonato, `${r}.atendio`),
    { pregunta: t(`${r}.atendioNombre`), respuesta: e.atendioNombre.trim() },
    { pregunta: t(`${r}.traslado`), respuesta: e.traslado ? t(`${r}.trasladoOpciones.${e.traslado}`) : '' },
    { pregunta: t(`${r}.tipificacionRn`), respuesta: e.tipificacionRn.trim() },
    { pregunta: t(`${r}.vitaminaK`), respuesta: etiquetaSiNo(t, opcionesSiNo, e.vitaminaK) },
    { pregunta: t(`${r}.profilaxisOcular`), respuesta: etiquetaSiNo(t, opcionesSiNo, e.profilaxisOcular) },
    { pregunta: t(`${r}.metodoCanguroAmerita`), respuesta: etiquetaSiNo(t, opcionesSiNo, e.metodoCanguroAmerita) },
    // "Se aplica" cuelga de "Amerita": si no amerita, no hay nada que aplicar.
    { pregunta: t(`${r}.metodoCanguroSeAplica`), respuesta: e.metodoCanguroAmerita === 'no' ? '' : etiquetaSiNo(t, opcionesSiNo, e.metodoCanguroSeAplica) },
    { pregunta: t(`${r}.examenFisicoNormal`), respuesta: etiquetaSiNo(t, opcionesSiNo, e.examenFisicoNormal) },
    { pregunta: t(`${r}.defectosCongenitos`), respuesta: e.defectosCongenitos ? t(`${r}.defectosCongenitosOpciones.${e.defectosCongenitos}`) : '' },
    { pregunta: t(`${r}.defectosCongenitosCie10`), respuesta: e.defectosCongenitos === 'no' ? '' : e.defectosCongenitosCie10.trim() },
    { pregunta: t(`${r}.enfermedades`), respuesta: e.enfermedades ? t(`${r}.enfermedadesOpciones.${e.enfermedades}`) : '' },
    { pregunta: t(`${r}.enfermedadesCie10Fila1`), respuesta: e.enfermedades === 'ninguna' ? '' : conCodigoCie(e.enfermedadesDx[0]) },
    { pregunta: t(`${r}.enfermedadesCie10Fila2`), respuesta: e.enfermedades === 'ninguna' ? '' : conCodigoCie(e.enfermedadesDx[1]) },
    { pregunta: t(`${r}.tamizajeMetabolico`), respuesta: e.tamizajeMetabolico ? t(`${r}.tamizajeMetabolicoOpciones.${e.tamizajeMetabolico}`) : '' },
    paresDeChecklist(t, `${r}.tamizajeAuditivo`, TAMIZAJE_AUDITIVO_OPCIONES, e.tamizajeAuditivo),
    { pregunta: t(`${r}.vacunaHepatitisBDosis0`), respuesta: etiquetaSiNo(t, opcionesSiNo, e.vacunaHepatitisBDosis0) },
    { pregunta: t(`${r}.apegoInmediato`), respuesta: etiquetaSiNo(t, opcionesSiNo, e.apegoInmediato) },
    { pregunta: t(`${r}.meconio1erDia`), respuesta: etiquetaSiNo(t, opcionesSiNo, e.meconio1erDia) },
    { pregunta: t(`${r}.lactanciaInmediata`), respuesta: etiquetaSiNo(t, opcionesSiNo, e.lactanciaInmediata) },
  );
  return pares;
}

// ---------- Egreso RN / Egreso materno ----------

export const CONDICION_EGRESO_RN_OPCIONES = ['vivo', 'fallece', 'referencia'] as const;
export type CondicionEgresoRn = (typeof CONDICION_EGRESO_RN_OPCIONES)[number];

export const ALIMENTO_AL_ALTA_OPCIONES = ['lactanciaExclusiva', 'parcial', 'artificial', 'lecheHumana'] as const;
export type AlimentoAlAlta = (typeof ALIMENTO_AL_ALTA_OPCIONES)[number];

export interface EstadoEgresoRn051 {
  readonly condicion: CondicionEgresoRn | '';
  readonly hora: string;
  readonly fecha: string;
  readonly falleceDuranteReferencia: string;
  readonly falleceEnLugarReferencia: string;
  readonly nombreEstablecimiento: string;
  readonly alimentoAlAlta: AlimentoAlAlta | '';
  readonly decubitoSupino: string;
  readonly edadDiasCompletos: string;
  /** Casilla "< 1 día" junto a los días completos. */
  readonly edadMenor1Dia: boolean;
  readonly bcg: string;
  /** Peso al egreso del recién nacido (g). */
  readonly pesoAlEgreso: string;
  readonly certificadoCcHcu: string;
  readonly nombreRecienNacido: string;
  readonly responsableEgresoRn: string;
}

export function estadoInicialEgresoRn051(): EstadoEgresoRn051 {
  return {
    condicion: '',
    hora: '',
    fecha: '',
    falleceDuranteReferencia: '',
    falleceEnLugarReferencia: '',
    nombreEstablecimiento: '',
    alimentoAlAlta: '',
    decubitoSupino: '',
    edadDiasCompletos: '',
    edadMenor1Dia: false,
    bcg: '',
    pesoAlEgreso: '',
    certificadoCcHcu: '',
    nombreRecienNacido: '',
    responsableEgresoRn: '',
  };
}

function paresDeEgresoRn051(t: Traducir, e: EstadoEgresoRn051): Par[] {
  return [
    { pregunta: t('formularios.f051.egresoRn.condicion'), respuesta: e.condicion ? t(`formularios.f051.egresoRn.condicionOpciones.${e.condicion}`) : '' },
    { pregunta: t('formularios.f051.egresoRn.hora'), respuesta: e.hora.trim() },
    { pregunta: t('formularios.f051.egresoRn.fecha'), respuesta: e.fecha.trim() },
    { pregunta: t('formularios.f051.egresoRn.falleceDuranteReferencia'), respuesta: etiquetaSiNo(t, 'formularios.f051.nearMiss.opcionesSiNo', e.falleceDuranteReferencia) },
    { pregunta: t('formularios.f051.egresoRn.falleceEnLugarReferencia'), respuesta: etiquetaSiNo(t, 'formularios.f051.nearMiss.opcionesSiNo', e.falleceEnLugarReferencia) },
    { pregunta: t('formularios.f051.egresoRn.nombreEstablecimiento'), respuesta: e.nombreEstablecimiento.trim() },
    { pregunta: t('formularios.f051.egresoRn.alimentoAlAlta'), respuesta: e.alimentoAlAlta ? t(`formularios.f051.egresoRn.alimentoAlAltaOpciones.${e.alimentoAlAlta}`) : '' },
    { pregunta: t('formularios.f051.egresoRn.decubitoSupino'), respuesta: etiquetaSiNo(t, 'formularios.f051.nearMiss.opcionesSiNo', e.decubitoSupino) },
    { pregunta: t('formularios.f051.egresoRn.edadMenor1Dia'), respuesta: si(t, e.edadMenor1Dia) },
    { pregunta: t('formularios.f051.egresoRn.edadDiasCompletos'), respuesta: e.edadMenor1Dia ? '' : e.edadDiasCompletos.trim() },
    { pregunta: t('formularios.f051.egresoRn.bcg'), respuesta: etiquetaSiNo(t, 'formularios.f051.nearMiss.opcionesSiNo', e.bcg) },
    { pregunta: t('formularios.f051.egresoRn.pesoAlEgreso'), respuesta: e.pesoAlEgreso.trim() },
    { pregunta: t('formularios.f051.egresoRn.certificadoCcHcu'), respuesta: e.certificadoCcHcu.trim() },
    { pregunta: t('formularios.f051.egresoRn.nombreRecienNacido'), respuesta: e.nombreRecienNacido.trim() },
    { pregunta: t('formularios.f051.egresoRn.responsableEgresoRn'), respuesta: e.responsableEgresoRn.trim() },
  ];
}

/** Métodos impresos en ANTICONCEPCIÓN, con su columna "pref." (preferido). */
export const METODO_ANTICONCEPCION_OPCIONES = ['barrera', 'diu', 'hormonal', 'natural', 'quirurgico', 'ninguno', 'otro'] as const;
export type MetodoAnticoncepcion = (typeof METODO_ANTICONCEPCION_OPCIONES)[number];

/** Métodos con columna "acced." (accedió) en el papel; Natural, Ninguno y Otro solo traen "pref.". */
export const METODO_ACCEDIDO_OPCIONES = ['barrera', 'diu', 'hormonal', 'quirurgico'] as const;

export const CONDICION_EGRESO_MATERNO_OPCIONES = ['viva', 'fallece', 'referencia'] as const;
export type CondicionEgresoMaterno = (typeof CONDICION_EGRESO_MATERNO_OPCIONES)[number];

export interface EstadoEgresoMaterno051 {
  readonly condicion: CondicionEgresoMaterno | '';
  readonly falleceDuranteReferencia: string;
  readonly falleceEnLugarReferencia: string;
  readonly hora: string;
  readonly fecha: string;
  readonly diasCompletosDesdeParto: string;
  readonly nombreEstablecimientoResponsable: string;
  readonly anticoncepcionAsesoria: string;
  /** Casillas "pref." por método (papel: círculos marcables). */
  readonly anticoncepcionPreferido: Readonly<Record<string, boolean>>;
  /** Casillas "acced." por método. */
  readonly anticoncepcionAccedido: Readonly<Record<string, boolean>>;
  readonly responsableEgresoMaterno: string;
}

export function estadoInicialEgresoMaterno051(): EstadoEgresoMaterno051 {
  return {
    condicion: '',
    falleceDuranteReferencia: '',
    falleceEnLugarReferencia: '',
    hora: '',
    fecha: '',
    diasCompletosDesdeParto: '',
    nombreEstablecimientoResponsable: '',
    anticoncepcionAsesoria: '',
    anticoncepcionPreferido: {},
    anticoncepcionAccedido: {},
    responsableEgresoMaterno: '',
  };
}

/** Lee lo guardado del egreso materno: el modelo viejo tenía UN método (`anticoncepcionMetodo`), que pasa a "preferido". */
export function leerEgresoMaterno051(valor: unknown): EstadoEgresoMaterno051 {
  const base = estadoInicialEgresoMaterno051();
  if (!esObjeto(valor)) return base;
  const preferido = leerMapaBool(valor.anticoncepcionPreferido);
  const viejo = valor.anticoncepcionMetodo;
  if (typeof viejo === 'string' && (METODO_ANTICONCEPCION_OPCIONES as readonly string[]).includes(viejo)) preferido[viejo] = true;
  const condicion = valor.condicion;
  return {
    condicion: condicion === 'viva' || condicion === 'fallece' || condicion === 'referencia' ? condicion : '',
    falleceDuranteReferencia: textoLibre(valor.falleceDuranteReferencia),
    falleceEnLugarReferencia: textoLibre(valor.falleceEnLugarReferencia),
    hora: textoLibre(valor.hora),
    fecha: textoLibre(valor.fecha),
    diasCompletosDesdeParto: textoLibre(valor.diasCompletosDesdeParto),
    nombreEstablecimientoResponsable: textoLibre(valor.nombreEstablecimientoResponsable),
    anticoncepcionAsesoria: textoLibre(valor.anticoncepcionAsesoria),
    anticoncepcionPreferido: preferido,
    anticoncepcionAccedido: leerMapaBool(valor.anticoncepcionAccedido),
    responsableEgresoMaterno: textoLibre(valor.responsableEgresoMaterno),
  };
}

function paresDeEgresoMaterno051(t: Traducir, e: EstadoEgresoMaterno051): Par[] {
  const m = 'formularios.f051.egresoMaterno';
  const opcionesSiNo = 'formularios.f051.nearMiss.opcionesSiNo';
  const pares: Par[] = [
    { pregunta: t(`${m}.condicion`), respuesta: e.condicion ? t(`${m}.condicionOpciones.${e.condicion}`) : '' },
    { pregunta: t(`${m}.hora`), respuesta: e.hora.trim() },
    { pregunta: t(`${m}.fecha`), respuesta: e.fecha.trim() },
    { pregunta: t(`${m}.falleceDuranteReferencia`), respuesta: etiquetaSiNo(t, opcionesSiNo, e.falleceDuranteReferencia) },
    { pregunta: t(`${m}.falleceEnLugarReferencia`), respuesta: etiquetaSiNo(t, opcionesSiNo, e.falleceEnLugarReferencia) },
    { pregunta: t(`${m}.diasCompletosDesdeParto`), respuesta: e.diasCompletosDesdeParto.trim() },
    { pregunta: t(`${m}.nombreEstablecimientoResponsable`), respuesta: e.nombreEstablecimientoResponsable.trim() },
    { pregunta: t(`${m}.anticoncepcionAsesoria`), respuesta: etiquetaSiNo(t, opcionesSiNo, e.anticoncepcionAsesoria) },
  ];
  // Sin asesoría ("No") no hay método que registrar: no se imprime para no firmar un dato huérfano.
  if (e.anticoncepcionAsesoria !== 'no') {
    pares.push(
      { pregunta: t(`${m}.anticoncepcionPreferido`), respuesta: METODO_ANTICONCEPCION_OPCIONES.filter((c) => e.anticoncepcionPreferido[c]).map((c) => t(`${m}.metodoOpciones.${c}`)).join(', ') },
      { pregunta: t(`${m}.anticoncepcionAccedido`), respuesta: METODO_ACCEDIDO_OPCIONES.filter((c) => e.anticoncepcionAccedido[c]).map((c) => t(`${m}.metodoOpciones.${c}`)).join(', ') },
    );
  }
  pares.push({ pregunta: t(`${m}.responsableEgresoMaterno`), respuesta: e.responsableEgresoMaterno.trim() });
  return pares;
}

// ---------- Estado combinado (Tanda 2, sin el partograma visual — ver `partograma/`) ----------

export interface EstadoPagina2_051 {
  readonly nearMiss: EstadoNearMiss051;
  readonly recienNacido: EstadoRecienNacido051;
  readonly egresoRn: EstadoEgresoRn051;
  readonly egresoMaterno: EstadoEgresoMaterno051;
}

export function estadoInicialPagina2_051(): EstadoPagina2_051 {
  return {
    nearMiss: estadoInicialNearMiss051(),
    recienNacido: estadoInicialRecienNacido051(),
    egresoRn: estadoInicialEgresoRn051(),
    egresoMaterno: estadoInicialEgresoMaterno051(),
  };
}

/** Serializa near miss/recién nacido/egresos a `Par[]`, listos para `onGuardado` — filtra respuestas vacías. */
export function paresDePagina2_051(t: Traducir, estado: EstadoPagina2_051): Par[] {
  const pares: Par[] = [
    ...paresDeNearMiss051(t, estado.nearMiss),
    ...paresDeRecienNacido051(t, estado.recienNacido),
    ...paresDeEgresoRn051(t, estado.egresoRn),
    ...paresDeEgresoMaterno051(t, estado.egresoMaterno),
  ];
  return pares.filter((p) => p.respuesta.trim() !== '');
}
